import { useEffect, useRef, useState } from "react";
import { NetworkStatus, useQuery } from "@apollo/client";
import usePagination from "@/hooks/usePagination";
import { useSearchWithParams } from "@/hooks/useSearchParams";
import { MEMBER_ELECTION_RUNS_QUERY } from "../queries";
import type {
  MemberElectionRunNode,
  MemberElectionRunsQueryResponse,
  MemberElectionRunsQueryVariables
} from "../types";

const EMPTY_RUNS: MemberElectionRunNode[] = [];

type UsePastMemberElectionRunsArgs = {
  deselectAll: () => void;
};

export function usePastMemberElectionRuns({ deselectAll }: UsePastMemberElectionRunsArgs) {
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "search"
  });

  const prevDebouncedSearchRef = useRef(debouncedSearch);
  const [settledSearch, setSettledSearch] = useState(debouncedSearch);

  useEffect(() => {
    if (prevDebouncedSearchRef.current !== debouncedSearch) {
      resetPagination();
      deselectAll();
    }
    prevDebouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch, resetPagination, deselectAll]);

  const queryVariables: MemberElectionRunsQueryVariables = {
    ...paginationVariables,
    ...(debouncedSearch ? { search: debouncedSearch } : {})
  };

  const searchActive = !!debouncedSearch;

  const { data, previousData, loading, error, refetch, networkStatus } = useQuery<
    MemberElectionRunsQueryResponse,
    MemberElectionRunsQueryVariables
  >(MEMBER_ELECTION_RUNS_QUERY, {
    variables: queryVariables,
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (!loading && !error) {
      setSettledSearch(debouncedSearch);
    }
  }, [loading, error, debouncedSearch]);

  const isUpdatingSearchResults = debouncedSearch !== settledSearch || networkStatus === NetworkStatus.refetch;
  const connection = isUpdatingSearchResults ? undefined : (data ?? previousData)?.membershipElectionRuns;
  const runs = connection?.nodes ?? EMPTY_RUNS;
  const showLoadError = !!error && !loading;

  return {
    runs,
    loading: (loading || isUpdatingSearchResults) && !error,
    showLoadError,
    refetch,
    searchValue,
    setSearchValue,
    clearSearch,
    searchActive,
    createHeaderPagination: createHeaderPagination(connection),
    createFooterPagination: createFooterPagination(connection)
  };
}
